import { useRef, type ElementType, type ReactNode } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useGSAP } from '@gsap/react';
import { DUR, EASE, DISTANCE, START, MQ } from '@/lib/motion';

gsap.registerPlugin(ScrollTrigger, useGSAP);

interface RevealProps {
  children: ReactNode;
  as?: ElementType;
  /** Travel distance in px. Halved on mobile. */
  y?: number;
  delay?: number;
  duration?: number;
  className?: string;
  /** ScrollTrigger start position. */
  start?: string;
}

/**
 * Fades and lifts an element into view on scroll.
 *
 * The initial hidden state is set by GSAP in JS, never in CSS. That matters:
 * if JS fails to run, the content is simply visible rather than permanently
 * invisible behind an animation that never fires.
 */
export function Reveal({
  children,
  as: Tag = 'div',
  y = DISTANCE.md,
  delay = 0,
  duration = DUR.base,
  className = '',
  start = START,
}: RevealProps) {
  const ref = useRef<HTMLElement>(null);

  useGSAP(
    () => {
      const el = ref.current;
      if (!el) return;

      const mm = gsap.matchMedia();

      mm.add(MQ.motion, () => {
        const isMobile = window.innerWidth < 768;
        gsap.set(el, { opacity: 0, y: isMobile ? y * 0.5 : y });

        // Already on screen at load (a hero, say)? Animate now — a scroll
        // trigger whose start is already behind the viewport never fires with
        // once: true, which would leave the element invisible for good.
        const alreadyVisible = el.getBoundingClientRect().top < window.innerHeight * 0.9;

        const tween = { opacity: 1, y: 0, duration, delay, ease: EASE.out };

        if (alreadyVisible) {
          gsap.to(el, tween);
        } else {
          gsap.to(el, { ...tween, scrollTrigger: { trigger: el, start, once: true } });
        }
      });

      // Reduced motion: no transform, no fade — just present.
      mm.add(MQ.reduced, () => {
        gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' });
      });

      // See SplitText: reverting on StrictMode's dev remount can leave the
      // element parked in its hidden state, so clear it explicitly.
      return () => {
        mm.revert();
        gsap.set(el, { opacity: 1, y: 0, clearProps: 'transform' });
      };
    },
    { scope: ref },
  );

  return (
    <Tag ref={ref} className={className}>
      {children}
    </Tag>
  );
}
